import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchAllPages, deletePage, permanentDeletePage, restorePage } from '../../../store/slices/cmsAdminSlice';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import toast from 'react-hot-toast';
import { renderSortIcon } from '../../../utils/sortIconHelper';
import { useAdminPermissions } from '../../../hooks/useAdminPermissions';

const CMSManagement = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pages = [], pagination = {}, loading, error } = useAppSelector((state) => state.cmsAdmin);
  const { perms } = useAdminPermissions();
  const canAdd = perms.cms?.add;
  const canEdit = perms.cms?.edit;
  const canDelete = perms.cms?.delete;

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('soft');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load pages on mount and when filters change
  useEffect(() => {
    dispatch(fetchAllPages({
      page: currentPage,
      limit: recordsPerPage,
      search: debouncedSearchTerm || undefined,
      sort_by: sortBy,
      sort_order: sortOrder
    }));
  }, [dispatch, currentPage, recordsPerPage, debouncedSearchTerm, sortBy, sortOrder]);

  const displayData = pages || [];
  const totalRecords = pagination?.totalRecords || displayData.length || 0;
  const totalPages = pagination?.totalPages || Math.ceil((displayData.length || 0) / recordsPerPage) || 1;
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = Math.min(startIndex + recordsPerPage, totalRecords);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const getSortIcon = (column) => {
    return renderSortIcon(column, sortBy, sortOrder);
  };

  const handleDeletePage = (pageId, isPermanent = false) => {
    setPageToDelete(pageId);
    setDeleteType(isPermanent ? 'permanent' : 'soft');
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (deleteType === 'permanent') {
        await dispatch(permanentDeletePage(pageToDelete)).unwrap();
        toast.success('Page permanently deleted successfully!');
      } else {
        await dispatch(deletePage(pageToDelete)).unwrap();
        toast.success('Page deleted successfully!');
      }
      // Refresh the pages list
      dispatch(fetchAllPages({
        page: currentPage,
        limit: recordsPerPage,
        search: debouncedSearchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder
      }));
    } catch (error) {
      toast.error(error || 'Failed to delete page');
    } finally {
      setShowDeleteConfirmation(false);
      setPageToDelete(null);
      setDeleteType('soft');
    }
  };

  const handleViewPage = (page) => {
    navigate(`/admin/cms/${page.id}`);
  };

  const handleEditPage = (page) => {
    navigate(`/admin/cms/${page.id}/edit`);
  };

  const handleRestorePage = async (pageId) => {
    try {
      await dispatch(restorePage(pageId)).unwrap();
      toast.success('Page restored successfully!');
      // Refresh the pages list
      dispatch(fetchAllPages({
        page: currentPage,
        limit: recordsPerPage,
        search: debouncedSearchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder
      }));
    } catch (error) {
      toast.error(error || 'Failed to restore page');
    }
  };

  return (
    <div className="p-4 w-full h-full flex flex-col bg-[#ecf0f5]">
      {/* Header Section - PHP Style */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-800">CMS</h1>
          {canAdd && (
            <button
              onClick={() => navigate('/admin/cms/create')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-black bg-gradient-to-br from-[#ffeb00] to-[#ffd200] rounded hover:opacity-90 transition-opacity shadow-sm"
            >
              <i className="fas fa-plus"></i>
              <span>Add Page</span>
            </button>
          )}
        </div>

        {/* Controls Bar - Matching PHP Layout */}
        <div className="bg-white rounded shadow-sm p-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">Display</span>
            <select
              value={recordsPerPage}
              onChange={handleRecordsPerPageChange}
              className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#ffd200]"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-xs text-gray-600">Records</span>
          </div>

          <div className='flex gap-3'>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">Search:</span>
              <input
                type="search"
                value={searchTerm}
                onChange={handleSearch}
                className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#ffd200]"
              />
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1 || loading}
                className="px-3 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading}
                    className={`px-3 py-1 text-xs border rounded transition-colors ${currentPage === pageNum
                      ? 'bg-[#ffd200] text-white border-[#ffd200]'
                      : 'border-gray-300 bg-white hover:bg-gray-50'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages || loading}
                className="px-3 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Table Section */}
      <div className="bg-white rounded shadow-sm flex-1 flex flex-col overflow-hidden">
        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Loading pages...
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-red-500">Error: {error}</div>
            </div>
          ) : displayData.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">No pages found</div>
            </div>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead className="bg-gray-100 border-b border-gray-300">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 w-12">#</th>
                  <th onClick={() => handleSort('page_name')} className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 flex-1 cursor-pointer hover:bg-gray-50">
                    Page Name {getSortIcon('page_name')}
                  </th>
                  <th onClick={() => handleSort('status')} className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 w-24 cursor-pointer hover:bg-gray-50">
                    Status {getSortIcon('status')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayData.map((page, index) => (
                  <tr key={page.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2 text-xs text-gray-700 border-r border-gray-200 text-center">
                      {startIndex + index + 1}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700 border-r border-gray-200">
                      {page.page_name}
                    </td>
                    <td className="px-3 py-2 text-xs border-r border-gray-200">
                      <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${(page.status === 'Published' || page.status === 'published')
                        ? 'bg-[#28a745] text-white'
                        : page.status === 'Draft'
                          ? 'bg-yellow-800 text-black'
                          : 'bg-gray-800 text-black'
                        }`}>
                        {page.status || 'Published'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="flex items-center gap-1 justify-end">
                        {canEdit && (
                          <button
                            onClick={() => handleEditPage(page)}
                            className="w-5 h-5 flex items-center justify-center rounded bg-[#ffd200] text-black hover:opacity-80 transition-opacity"
                            title="Edit"
                          >
                            <i className="fas fa-pencil-alt text-xs"></i>
                          </button>
                        )}

                        {canDelete && (
                          <button
                            onClick={() => handleDeletePage(page.id, false)}
                            className="w-5 h-5 flex items-center justify-center rounded bg-[#dd4b39] text-white hover:bg-[#c23321] transition-colors"
                            title="Delete"
                          >
                            <i className="fas fa-trash text-xs"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <UniversalConfirmModal
        isOpen={showDeleteConfirmation}
        title={deleteType === 'permanent' ? 'Permanently Delete Page' : 'Delete Page'}
        message={
          deleteType === 'permanent'
            ? 'Are you sure you want to permanently delete this page? This action cannot be undone.'
            : 'Are you sure you want to delete this page? You can restore it later.'
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowDeleteConfirmation(false);
          setPageToDelete(null);
          setDeleteType('soft');
        }}
        isDangerous={deleteType === 'permanent'}
      />
    </div>
  );
};

export default CMSManagement;
